const { maxBy } = require('lodash')
const Color = require('color')

const white = '#FFFFFF'
const black = '#000000'

/**
 * band colors with text colors updated as per accessibility requirements
 * @see https://www.figma.com/design/NStPQnysCrbPji8OJEn7zd/Colour-Changes?node-id=512-108&p=f&t=bmpXBhjAVeV7Rjgz-0
 */
const PERFORMANCE_BAND_COLORS = [
  { bandColor: '#576BA9', textColor: '#FFFFFF' },
  { bandColor: '#6491F2', textColor: '#000000' },
  { bandColor: '#C27400', textColor: '#000000' },
  { bandColor: '#27682A', textColor: '#FFFFFF' },
  { bandColor: '#449748', textColor: '#000000' },
  { bandColor: '#80780F', textColor: '#FFFFFF' },
  { bandColor: '#FFC107', textColor: '#000000' },
  { bandColor: '#B22222', textColor: '#FFFFFF' },
  { bandColor: '#7C0A02', textColor: '#FFFFFF' },
  { bandColor: '#CB34AA', textColor: '#FFFFFF' },
  { bandColor: '#7C4DFF', textColor: '#FFFFFF' },
  { bandColor: '#AF75FF', textColor: '#000000' },
  { bandColor: '#C28800', textColor: '#000000' },
  { bandColor: '#00838F', textColor: '#FFFFFF' },
  { bandColor: '#26C6DA', textColor: '#000000' },
  { bandColor: '#2563EB', textColor: '#FFFFFF' },
]

const bandColors = PERFORMANCE_BAND_COLORS.map(({ bandColor }) => bandColor)

/**
 * Range fill colors for percentile data in reports that cannot be associated to any performance/proficiency band
 * @see https://www.figma.com/design/NStPQnysCrbPji8OJEn7zd/Colour-Changes?node-id=626-9788&t=jvBCTpDcp1bGJQpo-0
 */
const RANGE_FILL_COLORS = [
  { bandColor: '#B22222', textColor: '#FFFFFF', threshold: 20 },
  { bandColor: '#D93802', textColor: '#FFFFFF', threshold: 40 },
  { bandColor: '#C27400', textColor: '#000000', threshold: 60 },
  { bandColor: '#27682A', textColor: '#FFFFFF', threshold: 80 },
  { bandColor: '#2563EB', textColor: '#FFFFFF', threshold: 100 },
]

// NOTE: band with updated colors introduced for better display of foreground text for external performance bands
const bandColorsNew = [
  '#798CCE',
  '#A8C2E6',
  '#5FAD5A',
  '#90DE85',
  '#E9DC6B',
  '#ADA439',
  '#E69736',
  '#F59D7C',
  '#E55C5C',
  '#CE7BC1',
]

const colorBandsByLength = {
  2: ['#90DE85', '#E55C5C'],
  3: ['#90DE85', '#E9DC6B', '#E55C5C'],
  4: ['#5FAD5A', '#90DE85', '#E9DC6B', '#E55C5C'],
  5: ['#5FAD5A', '#90DE85', '#E9DC6B', '#E69736', '#E55C5C'],
  6: ['#5FAD5A', '#90DE85', '#E9DC6B', '#F59D7C', '#E69736', '#E55C5C'],
  7: [
    '#5FAD5A',
    '#90DE85',
    '#E9DC6B',
    '#ADA439',
    '#F59D7C',
    '#E69736',
    '#E55C5C',
  ],
  8: [
    '#5FAD5A',
    '#90DE85',
    '#A8C2E6',
    '#E9DC6B',
    '#ADA439',
    '#F59D7C',
    '#E69736',
    '#E55C5C',
  ],
  9: [
    '#5FAD5A',
    '#90DE85',
    '#798CCE',
    '#A8C2E6',
    '#E9DC6B',
    '#ADA439',
    '#F59D7C',
    '#E69736',
    '#E55C5C',
  ],
  10: [
    '#5FAD5A',
    '#90DE85',
    '#CE7BC1',
    '#798CCE',
    '#A8C2E6',
    '#E9DC6B',
    '#ADA439',
    '#F59D7C',
    '#E69736',
    '#E55C5C',
  ],
}

const getColorsByInterpolation = ({
  count = 0, // number of colors to generate
  fromColor = [116, 34, 52], // hsl -> #5FAD5A
  toColor = [0, 72, 63], // hsl -> #E55C5C
}) => {
  const [h0, s0, l0] = fromColor
  const [hn, sn, ln] = toColor

  const colors = Array(count)
    .fill()
    .map((_, i) => [
      h0 + (i * (hn - h0)) / (count - 1),
      s0 + (i * (sn - s0)) / (count - 1),
      l0 + (i * (ln - l0)) / (count - 1),
    ])
  return colors.map(([h, s, l]) => `hsl(${h}deg ${s}% ${l}%)`)
}

const getColorBandBySize = (length) => {
  if (length in colorBandsByLength) return [...colorBandsByLength[length]]

  if (length > bandColorsNew.length)
    return getColorsByInterpolation({
      count: length,
    })
  const [start, end] =
    length > bandColorsNew.length - 3
      ? [0, bandColorsNew.length - 1]
      : [2, bandColorsNew.length - 2]
  const colorBand = new Array(length).fill().map((_, i) => {
    const idx = Math.round(start + (i * (end - start)) / (length - 1))
    return bandColorsNew[idx]
  })

  colorBandsByLength[length] = colorBand
  return [...colorBand]
}

/**
 * Get the most visible foreground color from the list of foreground colors on the background color
 * @template {string} T
 * @param {T[]} fgColorList list of foreground colors to choose from
 * @param {string} bgColorStr background color
 * @returns {T} most visible foreground color
 */
const pickFGColor = (fgColorList, bgColorStr) => {
  const bgColor = Color(bgColorStr)
  const mostVisibleFGColor = maxBy(fgColorList, (fgColor) =>
    bgColor.contrast(Color(fgColor))
  )
  return mostVisibleFGColor
}

/**
 * Get the foreground color which is most visible on the background color
 * @param {string} bgColorStr - Background color string. Could be hex, rgb, rgba, hsl, hsla or named color
 */
const getFGColor = (bgColorStr) => {
  bgColorStr = bgColorStr || white
  return pickFGColor([black, white], bgColorStr)
}

const getTextColorForBandColor = (
  color,
  colorsInfo = PERFORMANCE_BAND_COLORS
) => {
  const colorInfo = colorsInfo.find(({ bandColor }) => bandColor === color)
  return colorInfo?.textColor || getFGColor(color)
}

const getTextColorForRangeFillColor = (color) => {
  const textColor = getTextColorForBandColor(color, RANGE_FILL_COLORS)
  return textColor
}

module.exports = {
  PERFORMANCE_BAND_COLORS,
  RANGE_FILL_COLORS,
  performanceBandColors: bandColors,
  externalPerformanceBandColors: bandColorsNew,
  standardProficiencyColors: bandColors,
  getColorBandBySize,
  getColorsByInterpolation,
  getTextColorForBandColor,
  getTextColorForRangeFillColor,
  getFGColor,
  pickFGColor,
}
